import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { fmtDate, fmtMoney } from "@/lib/format";
import { AlertTriangle, CalendarSearch, Link2, Unlink } from "lucide-react";

export const useStatementChain = (bankAccountId, refreshKey) => {
  const [chain, setChain] = useState({ statements: {}, issues: [] });
  useEffect(() => {
    api.get("/statements/chain", { params: bankAccountId ? { bank_account_id: bankAccountId } : {} })
      .then((r) => setChain(r.data))
      .catch(() => setChain({ statements: {}, issues: [] }));
  }, [bankAccountId, refreshKey]);
  return chain;
};

export const issueText = (i) => (i.type === "gap"
  ? `Missing period ${fmtDate(i.start)}–${fmtDate(i.end)} (between ${i.prev_file_name} and ${i.file_name})`
  : `Chain break · off by ${fmtMoney(i.difference)} — ${i.prev_file_name} ended ${fmtMoney(i.prev_closing)}, ${i.file_name} begins ${fmtMoney(i.opening)}`);

export function ChainBadge({ entry, id }) {
  if (!entry || entry.status === "first" || entry.status === "duplicate") return null;
  const gap = entry.missing_before;
  if (entry.status === "ok") {
    return (
      <span data-testid={`statement-chain-ok-${id}`} title={`Begins where ${entry.prev_file_name} ended (${fmtMoney(entry.prev_closing)})`}>
        <Link2 size={13} className="text-[var(--ll-success)]" />
      </span>
    );
  }
  const badge = (testid, text, title) => (
    <span
      data-testid={testid}
      title={title}
      className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 inline-flex items-center gap-1 whitespace-nowrap"
      style={{ background: "#FEF2F2", color: "var(--ll-error)", border: "1px solid var(--ll-error)" }}
    >
      <Unlink size={10} /> {text}
    </span>
  );
  return (
    <>
      {gap && badge(`statement-chain-gap-${id}`, `Missing ${fmtDate(gap.start)}–${fmtDate(gap.end)}`,
        `No statement covers ${fmtDate(gap.start)}–${fmtDate(gap.end)} (after ${entry.prev_file_name})`)}
      {entry.status === "break" && badge(`statement-chain-break-${id}`, `Chain break · off by ${fmtMoney(entry.difference)}`,
        `Previous ending (${fmtDate(entry.prev_period_end)} · ${entry.prev_file_name}) ${fmtMoney(entry.prev_closing)} vs this beginning ${fmtMoney(entry.opening)}`)}
    </>
  );
}

export function ReconChainWarnings({ accountId, range, refreshKey, onJump }) {
  const { issues } = useStatementChain(accountId, refreshKey);
  const visible = issues.filter((i) => (!range.end || i.start <= range.end) && (!range.start || i.end >= range.start));
  if (!accountId || visible.length === 0) return null;
  return (
    <div className="mb-5 p-4 border" style={{ background: "#FFF7ED", borderColor: "#F59E0B" }} data-testid="recon-chain-warnings">
      <div className="flex items-center gap-2 text-sm font-medium text-[#B45309] mb-2">
        <AlertTriangle size={16} /> Balance chain: {visible.length} issue{visible.length === 1 ? "" : "s"} in this range
      </div>
      <ul className="space-y-1.5">
        {visible.map((i) => (
          <li key={`${i.type}-${i.statement_id}`} className="flex items-center justify-between gap-3 text-sm" data-testid={`recon-chain-issue-${i.type}-${i.statement_id}`}>
            <span>{issueText(i)}</span>
            <button
              onClick={() => onJump(fmtDate(i.start), fmtDate(i.end))}
              data-testid={`recon-chain-jump-${i.type}-${i.statement_id}`}
              className="shrink-0 text-xs px-2 py-1 border bg-white hover:bg-[var(--ll-mint)] inline-flex items-center gap-1 transition-colors"
              style={{ borderColor: "var(--ll-border)" }}
            >
              <CalendarSearch size={12} /> Jump to range
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
